import React, { useContext, useState, useEffect, useRef } from "react";
import { GlobalVariablesContext } from "../../../GlobalVariables";
import axios from "axios";
import Messages from "./Messages";
import Template from "./Template";

function Body() {
  const [ln, setln] = useState(0);
  const [newMessages, setNewMessages] = useState([]);
  const { currentUser } = useContext(GlobalVariablesContext);
  const [loading, setLoading] = useState(false);
  const [activeHandover, setActiveHandover] = useState(null); // independently polled
  const isMounted = useRef(true);

  // Mark mounted/unmounted to prevent memory leaks
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchMessages = React.useCallback(async () => {
    if (
      (!currentUser.conversation_id && (!currentUser.phone || !currentUser.wb_num))
    ) return;

    try {
      let receivedMessages = [];

      // --- Independently fetch handover status every 3s ---
      // Do NOT rely on currentUser.handover_data (stale snapshot from click time)
      let handoverData = null;
      try {
        const hvRes = await axios.get('https://casualfootwears.com/api/human_handovers/');
        const handovers = hvRes.data || [];
        // session_id in DB = phone number (for WA) or user_id (for Meta)
        const matched = handovers.find(h =>
          String(h.session_id) === String(currentUser.phone)
        );
        if (matched) {
          handoverData = matched;
          if (isMounted.current) setActiveHandover(matched);
        } else {
          if (isMounted.current) setActiveHandover(null);
        }
      } catch (hvErr) {
        // Non-fatal: fall back to snapshot from when user clicked chat
        handoverData = currentUser.handover_data || null;
      }

      if (currentUser.platform === 'instagram' || currentUser.platform === 'facebook' || currentUser.platform === 'page') {
        const response = await axios.get(`https://jutiyapa.com/api/conversations/${currentUser.conversation_id}/messages/`);
        const sortedData = response.data.reverse();

        receivedMessages = sortedData.map((msg, index) => {
          let sentByVal = "sent";
          if (msg.message_type === 'received') sentByVal = "Customer";

          return {
            name: msg.sender?.username || msg.sender?.full_name || "Unknown User",
            phone_number: msg.sender?.user_id,
            profile_picture: (() => {
              if (currentUser.platform === 'instagram' && msg.sender?.profile_picture_url) {
                const pp = msg.sender.profile_picture_url;
                return pp.startsWith("/media/") ? "http://jutiyapa.com" + pp : pp;
              }
              return null;
            })(),
            message: msg.message_text || "",
            timestamp: new Date(msg.timestamp).getTime() / 1000,
            msg_status_comment: null,
            msg_status_code: msg.is_read ? 'read' : 'delivered',
            sent_by: sentByVal,
            media_upload: msg.media_url,
            media_type: msg.media_type,
            admin_seen_count: msg.is_read ? 1 : 0,
            message_id: msg.message_id || `meta_${index}_${Date.now()}`,
            is_template: false,
            wp_template_json: null,
            template_json: null,
            platform: currentUser.platform,
          };
        });

      } else {
        const roomUrl = `https://casualfootwears.com/api/react/v2/${currentUser.phone}/${currentUser.wb_num}/`;
        const response = await axios.get(roomUrl);

        receivedMessages = response.data.map((message, index) => ({
          name: message.profile_name,
          phone_number: message.phone_number,
          message: message.text,
          timestamp: message.timestamp,
          msg_status_comment: message.msg_status_comment,
          msg_status_code: message.msg_status_code,
          sent_by: message.message_text_sent_by,
          media_upload: message.upload_media_path,
          admin_seen_count: message.admin_seen_count,
          message_id: message.message_id || `wa_${index}_${Date.now()}`,
          is_template: message.is_template,
          wp_template_json: message.wp_template_json,
          template_json: message.template_json,
        }));
      }

      // Inject AI Handover system message if there is an active handover
      if (handoverData) {
        const h = handoverData;
        const handoverTimestamp = h.timestamp
          ? new Date(h.timestamp).getTime() / 1000
          : Date.now() / 1000;
        receivedMessages.push({
          name: "AI System",
          phone_number: "SYSTEM",
          message: `🚨 AI transferred this chat to human agent`,  // title only
          handover_message: h.message || null,    // AI reason — shown separately
          handover_context: (h.context && h.context !== 'None') ? h.context : null,  // chat context — shown separately
          timestamp: handoverTimestamp + 1,
          msg_status_code: 'read',
          sent_by: "HANDOVER_SYSTEM",
          message_id: `handover_sys_${h.session_id || Date.now()}`,
          is_template: false,
          platform: currentUser.platform,
          session_id: h.session_id,
          is_handover_system_msg: true,
        });
      }

      // Sort ascending by timestamp: oldest at top, newest at bottom
      receivedMessages.sort((a, b) => a.timestamp - b.timestamp);

      if (isMounted.current) {
        const newStr = JSON.stringify(receivedMessages);
        setNewMessages((prev) => {
          if (JSON.stringify(prev) !== newStr) {
            return [...receivedMessages];
          }
          return prev;
        });

        if (ln < receivedMessages.length) {
          setln(receivedMessages.length);
        }
      }

    } catch (error) {
      console.error('[BODY] FetchMessages error:', error);
    }
  }, [currentUser, ln]);

  // Polling Effect - every 3 seconds
  useEffect(() => {
    fetchMessages();
    const intervalId = setInterval(() => {
      fetchMessages();
    }, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    const chatContainer = document.querySelector(".Chat__bodyOfMsg");
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, [ln]);

  // Reset on chat switch
  useEffect(() => {
    setln(0);
    setNewMessages([]);
    setActiveHandover(null);
    setLoading(true);

    // Mark WhatsApp messages as read
    if (currentUser.phone && currentUser.wb_num && currentUser.platform === 'whatsapp') {
      axios.post('https://casualfootwears.com/api/mark_chat_read/', {
        phone_number: currentUser.phone,
        whatsapp_bussiness_number: currentUser.wb_num
      }).catch(err => console.error('[BODY] Read Status Error:', err));
    }

    setTimeout(() => { if (isMounted.current) setLoading(false) }, 500);

  }, [currentUser]);


  return (
    <>
      {loading ? (
        <div className="loadingText">Loading...</div>
      ) : (
        newMessages.length > 0 ? (
          newMessages.map((message) =>
            message.is_template ? (
              <Template message={message} key={message.message_id} />
            ) : (
              <Messages message={message} key={message.message_id} />
            )
          )
        ) : (
          <div className="loadingText">No messages</div>
        )
      )}
    </>
  );
}

export default Body;
